import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import Select from './ui/Select';
import FormField from './ui/FormField';
import { roleLabel } from '../utils/roleLabel';
import { branchLabel } from '../utils/authorizedBranches';
import { appLocale } from '../utils/locale';
import { companyHasFeature } from '../utils/planFeatures';

/**
 * Per-branch roles (roles design §2.10). Only ever rendered while the company
 * has `per_branch_roles` on; with the switch off nothing here mounts and no
 * request is made.
 */

/**
 * The role a branch row of /users/me/branches carries, when it is NOT the
 * user's default role — the only case the pickers label (`ubr.roleInBranch`).
 * `defaultRole` comes from /auth/me and is sent only for a user with overrides,
 * so a user without any (and everyone with the switch off) gets null for every
 * branch.
 */
export function differingBranchRole(branch, defaultRole) {
  const role = branch?.role;
  if (!defaultRole || !role?.id || String(role.id) === String(defaultRole.id)) return null;
  return role;
}

/**
 * The override stored on one row of a user's `authorizedGarages`. The users API
 * returns it as a through-attribute (`UserGarage.role_id`) while the switch is
 * on; a flat `role_id` is read too so either serialisation works.
 */
export function storedBranchRoleId(garage) {
  return garage?.UserGarage?.role_id ?? garage?.user_garage?.role_id ?? garage?.role_id ?? null;
}

/** `{ garageId: roleId }` of a user's stored overrides. */
export function storedBranchRoles(user) {
  const out = {};
  (user?.authorizedGarages || []).forEach((g) => {
    if (storedBranchRoleId(g)) out[g.id] = storedBranchRoleId(g);
  });
  return out;
}

/**
 * The `branch_roles` body for the shown non-home rows, or null when no row
 * changed: a PUT body without it keeps the stored overrides, and an unchanged
 * echo would only re-run the server's checks.
 */
export function branchRolesChange(rows, homeId, value, initial) {
  const body = rows
    .filter((g) => g.id !== homeId)
    .map((g) => ({ garage_id: g.id, role_id: value[g.id] || null }));
  return body.some((r) => r.role_id !== (initial[r.garage_id] || null)) ? body : null;
}

/**
 * The user modal's per-branch role state. `value` is `{ garageId: roleId }`
 * for the overridden branches; a branch missing from it wears the default
 * role. Rows are the selected branches, or every company branch for a
 * company-level target (no home branch). The table shows when the switch is on
 * and there is more than one row (or the target is company-level).
 */
export function useBranchRoles({
  currentUser,
  isSuperAdminRole,
  user,
  isOpen,
  homeId,
  garages,
  selectedGarageIds,
}) {
  const enabled = companyHasFeature(currentUser?.company, 'per_branch_roles');
  const [value, setValue] = useState({});
  const [initial, setInitial] = useState({});
  const [tab, setTab] = useState('details');

  useEffect(() => {
    setValue(storedBranchRoles(user));
    setInitial(storedBranchRoles(user));
    setTab('details');
  }, [user, isOpen]);

  // The home branch always wears the default role (the server clears an
  // override on a new home too), so moving the home drops its override here.
  useEffect(() => {
    setValue((r) => {
      if (!r[homeId]) return r;
      const next = { ...r };
      delete next[homeId];
      return next;
    });
  }, [homeId]);

  const companyLevel = !!user && !homeId && !(user.garage?.id || user.garage_id);
  const rows = companyLevel ? garages : garages.filter((g) => selectedGarageIds.includes(g.id));
  const show = enabled && !isSuperAdminRole && (companyLevel || rows.length > 1);
  const changed = () => (show ? branchRolesChange(rows, homeId, value, initial) : null);
  const table = { branches: rows, homeId, value, onChange: setValue };
  return { enabled, show, table, changed, tab, setTab };
}

/**
 * The user modal's two tabs (edit form, role history) and the history list;
 * switch ON and an existing user only. The form stays mounted (hidden) while
 * the history shows, so nothing typed is lost.
 */
export function UserModalTabs({ br, user, garages }) {
  const { t } = useTranslation();
  if (!br.enabled || !user) return null;
  const { tab, setTab: onTab } = br;
  return (
    <>
      <div className="flex gap-2 mb-4 border-b border-edge" role="tablist">
        {[
          ['details', t('editUser')],
          ['history', t('ubr.history')],
        ].map(([key, label]) => (
          <button
            key={key}
            type="button"
            role="tab"
            aria-selected={tab === key}
            onClick={() => onTab(key)}
            className={`px-3 py-2 text-sm -mb-px border-b-2 ${
              tab === key
                ? 'border-accent text-accent font-medium'
                : 'border-transparent text-content-muted'
            }`}
          >
            {label}
          </button>
        ))}
      </div>
      {tab === 'history' && <RoleHistoryList userId={user.id} garages={garages} />}
    </>
  );
}

/**
 * The "Role per branch" table of the user modal.
 * - one row per branch in `branches`; the home branch row is read-only and
 *   always shows the default role;
 * - every other row picks "Same as default role (X)" (null) or a tenant role;
 * - when the resulting roles differ between branches, a note says the user
 *   will not be able to use "All branches" (the server refuses it for them).
 */
export default function BranchRolesTable({
  branches = [],
  homeId = '',
  defaultRole = null,
  roleOptions = [],
  value = {},
  onChange,
  error,
}) {
  const { t } = useTranslation();
  const defaultName = defaultRole ? roleLabel(defaultRole) : '—';

  const effective = new Set(
    branches.map((b) => (b.id === homeId ? '' : value[b.id] || '') || String(defaultRole?.id || ''))
  );
  const mixed = effective.size > 1;

  return (
    <FormField label={t('ubr.title')} error={error}>
      <div
        className="border border-edge-strong rounded-lg divide-y divide-edge"
        data-testid="branch-roles-table"
      >
        {branches.map((b) => {
          const isHome = b.id === homeId;
          return (
            <div key={b.id} className="flex items-center gap-3 p-3">
              <span className="text-sm text-content-secondary flex-1 min-w-0 truncate">
                {branchLabel(b)}
              </span>
              {isHome ? (
                <span className="text-xs text-content-muted text-end shrink-0">
                  {defaultName} · {t('ubr.homeUsesDefault')}
                </span>
              ) : (
                <div className="w-48 shrink-0">
                  <Select
                    aria-label={branchLabel(b)}
                    value={value[b.id] || ''}
                    onChange={(e) => {
                      const roleId = e.target.value;
                      onChange((r) => {
                        const next = { ...r };
                        if (roleId) next[b.id] = roleId;
                        else delete next[b.id];
                        return next;
                      });
                    }}
                    invalid={!!error}
                  >
                    <option value="">{t('ubr.sameAsDefault', { role: defaultName })}</option>
                    {roleOptions.map((r) => (
                      <option key={r.id} value={r.id}>
                        {roleLabel(r)}
                      </option>
                    ))}
                  </Select>
                </div>
              )}
            </div>
          );
        })}
      </div>
      {mixed && (
        <p className="mt-2 text-xs text-amber-700 dark:text-amber-300">
          {t('ubr.allBranchesBlocked')}
        </p>
      )}
    </FormField>
  );
}

/**
 * The user modal's history tab: GET /api/users/:id/role-history (newest first,
 * at most 100 rows of `user.role.set` / `user.branch_role.set`). A removed
 * branch carries `new_values: null`; it shows as a dash.
 */
export function RoleHistoryList({ userId, garages = [] }) {
  const { t, i18n } = useTranslation();
  const [rows, setRows] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let alive = true;
    setRows(null);
    setFailed(false);
    axios
      .get(`/api/users/${userId}/role-history`)
      .then((res) => {
        if (alive) setRows(res.data?.data || []);
      })
      .catch(() => {
        if (alive) setFailed(true);
      });
    return () => {
      alive = false;
    };
  }, [userId]);

  if (failed) return <p className="text-sm text-red-600 p-3">{t('error')}</p>;
  if (!rows) return <p className="text-sm text-content-muted p-3">{t('loading')}</p>;
  if (rows.length === 0) return <p className="text-sm text-content-muted p-3">{t('noData')}</p>;

  const roleName = (v) =>
    v ? (i18n.language === 'ar' && v.role_name_ar) || v.role_name || '—' : '—';
  const where = (row) => {
    if (row.action === 'user.role.set') return t('role');
    const g = garages.find((x) => String(x.id) === String(row.garage_id));
    return g ? branchLabel(g) : t('branch');
  };

  return (
    <ul className="divide-y divide-edge border border-edge rounded-lg" data-testid="role-history">
      {rows.map((row) => (
        <li key={row.id} className="p-3 text-sm">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <span className="font-medium text-content">{where(row)}</span>
            <span className="text-xs text-content-muted tabular-nums">
              {row.created_at
                ? new Date(row.created_at).toLocaleString(appLocale(i18n.language))
                : ''}
            </span>
          </div>
          <div className="mt-0.5 text-content-secondary">
            {/* U+2192 is not mirrored in RTL: Arabic reads old ← new the other way. */}
            {roleName(row.old_values)} {i18n.language === 'ar' ? '←' : '→'}{' '}
            {roleName(row.new_values)}
          </div>
          {row.actor_name && <div className="text-xs text-content-muted">{row.actor_name}</div>}
        </li>
      ))}
    </ul>
  );
}
